import * as React from "react";

import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export type ParamNumberSliderFieldProps = {
  id: string;
  label: string;
  description?: string | null;
  value: number;
  onChange: (next: number) => void;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  disabled?: boolean;
  className?: string;
};

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

export function ParamNumberSliderField({
  id,
  label,
  description,
  value,
  onChange,
  min,
  max,
  step = 1,
  unit,
  disabled,
  className,
}: ParamNumberSliderFieldProps) {
  const safeValue = Number.isFinite(value) ? clamp(value, min, max) : min;

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-0.5">
          <Label htmlFor={id} className="text-sm">
            {label}
          </Label>
          {description ? (
            <p className="text-xs text-muted-foreground">{description}</p>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <Input
            id={id}
            type="number"
            inputMode="decimal"
            value={Number.isFinite(safeValue) ? safeValue : ""}
            step={step}
            min={min}
            max={max}
            disabled={disabled}
            onChange={(e) => {
              const raw = e.target.value;
              const n = raw === "" ? NaN : Number(raw);
              if (!Number.isFinite(n)) return;
              onChange(clamp(n, min, max));
            }}
            className="w-28"
          />
          {unit ? <span className="text-xs text-muted-foreground">{unit}</span> : null}
        </div>
      </div>

      <Slider
        value={[safeValue]}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onValueChange={(vals) => {
          const next = vals?.[0];
          if (typeof next !== "number") return;
          onChange(clamp(next, min, max));
        }}
      />
    </div>
  );
}
